import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  BulkPublishResponse,
  ProductSelection,
  StoreProductStatus,
} from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { ProductPicker, type OrgProductRow } from '@/features/resource-pickers';
import { productTitles } from '@/shared/bulk/product-titles';
import { selectionChanged } from '@/shared/bulk/selection-errors';
import { useBulkErrorMessage } from '@/shared/bulk/use-bulk-error-message';
import { useSelectionRequest } from '@/shared/bulk/use-selection-request';

import { useBulkPublish } from '../hooks/use-store-product-mutations';
import { groupSkipped } from '../schemas/publication-rules';

export interface AddProductsDialogProps {
  storeId: string;
  /** The store catalog tour points at this instance (the page header's, not the empty state's). */
  tour?: boolean;
}

type PublishAs = Extract<StoreProductStatus, 'draft' | 'published'>;

/** Archived master products cannot join a store, so the picker never lists them. */
const PICKABLE = ['active', 'draft'] as const;

interface Outcome {
  response: BulkPublishResponse;
  titles: ReadonlyMap<string, string>;
  /** Sent as "all matching": counted as added and failed of the matched products. */
  matching: boolean;
}

function SkippedSummary({ response, titles: known, matching }: Outcome) {
  const { t, i18n } = useTranslation(['storeCatalog', 'common']);
  const groups = groupSkipped(response);
  // Skipped products of "all matching" mostly never loaded in the picker.
  const missing = response.skipped.map((s) => s.productId).filter((id) => !known.has(id));
  const looked = useQuery({
    queryKey: ['bulk-result-titles', missing],
    queryFn: () => productTitles(missing),
    enabled: missing.length > 0,
    staleTime: Infinity,
    meta: { silentError: true },
  });
  const titleOf = (id: string) => known.get(id) ?? looked.data?.get(id) ?? id;
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <Alert variant={groups.length > 0 ? 'destructive' : 'default'} role="status">
      <AlertTitle>
        {matching
          ? t('common:bulkResult.matchedTitle', {
              succeeded: number.format(response.matched - response.skipped.length),
              failed: number.format(response.skipped.length),
            })
          : t('storeCatalog:add.result.title', {
              created: response.created,
              updated: response.updated,
            })}
      </AlertTitle>
      {groups.length > 0 ? (
        <AlertDescription>
          <ul className="list-disc pl-4">
            {groups.map((group) => (
              <li key={group.reason}>
                {t(`storeCatalog:add.result.skipped.${group.reason}`, {
                  count: group.productIds.length,
                })}
                {': '}
                {group.productIds.map(titleOf).join(', ')}
              </li>
            ))}
          </ul>
        </AlertDescription>
      ) : null}
    </Alert>
  );
}

/**
 * "Add products" on the store catalog: the master catalog in the product picker, with "Add as"
 * (published or draft) under the list, then one bulk-publish call with the picker's selection,
 * ticked products or every matching one (D-182). Products already in the store are locked by the
 * list itself (`memberOf`) and left out of "all matching", so the call only creates rows and never
 * changes the status of a publication someone set on purpose. What the backend skipped (not
 * active, handle taken) is listed by reason afterwards so the master product can be fixed.
 */
export function AddProductsDialog({ storeId, tour }: AddProductsDialogProps) {
  const { t } = useTranslation(['storeCatalog', 'common']);
  const idPrefix = useId();
  const message = useBulkErrorMessage();
  const publish = useBulkPublish(storeId);
  const selectionRequest = useSelectionRequest();
  const [open, setOpen] = useState(false);
  const [publishAs, setPublishAs] = useState<PublishAs>('published');
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setPublishAs('published');
      publish.reset();
    }
  };

  const confirm = async (items: OrgProductRow[], picked: ProductSelection) => {
    // Paste already leaves members out; a row known to be in the store is never sent either,
    // since publishing it again would rewrite the status someone set on purpose.
    const members = new Set(items.filter((p) => p.isMember).map((p) => p.id));
    const selection: ProductSelection =
      picked.mode === 'ids'
        ? { ...picked, ids: picked.ids.filter((id) => !members.has(id)) }
        : picked;
    if (selection.mode === 'ids' && selection.ids.length === 0) return true;
    try {
      const response = await selectionRequest.run(selection, (body) =>
        publish.mutateAsync({ selection: body, status: publishAs }),
      );
      if (response === null) {
        publish.reset();
        return false;
      }
      setOutcome({
        response,
        titles: new Map(items.map((p) => [p.id, p.title])),
        matching: selection.mode === 'query',
      });
      return true;
    } catch {
      // The alert under the list shows the error; the picker stays open to try again.
      return false;
    }
  };

  const footer = (items: OrgProductRow[]) => {
    const inactive = items.filter((p) => p.status !== 'active').length;
    return (
      <div className="flex flex-col gap-3">
        <RadioGroup
          value={publishAs}
          onValueChange={(v) => setPublishAs(v as PublishAs)}
          className="grid gap-2 sm:grid-cols-2"
          aria-label={t('storeCatalog:add.publishAs')}
        >
          {(['published', 'draft'] as const).map((status) => (
            <label
              key={status}
              htmlFor={`${idPrefix}-as-${status}`}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-md border p-3',
                publishAs === status && 'border-primary bg-primary-tint/40',
              )}
            >
              <RadioGroupItem id={`${idPrefix}-as-${status}`} value={status} className="mt-0.5" />
              <span className="grid gap-0.5 text-sm">
                <span className="font-medium">{t(`storeCatalog:status.${status}`)}</span>
                <span className="text-muted-foreground text-xs">
                  {t(`storeCatalog:add.publishAsHint.${status}`)}
                </span>
              </span>
            </label>
          ))}
        </RadioGroup>
        {publishAs === 'published' && inactive > 0 ? (
          <p className="text-warning-foreground dark:text-warning text-xs">
            {t('storeCatalog:add.inactiveWarning', { count: inactive })}
          </p>
        ) : null}
        {publish.isError && !selectionChanged(publish.error) ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {message(publish.error)}
              {isApiError(publish.error) && publish.error.requestId ? (
                <span className="block text-xs">
                  {t('errors:requestIdShort', { requestId: publish.error.requestId })}
                </span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
      </div>
    );
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        data-tour={tour ? 'store-catalog.list.add-button' : undefined}
      >
        <Plus aria-hidden />
        {t('storeCatalog:add.action')}
      </Button>
      <ProductPicker
        scope={{ kind: 'org' }}
        open={open}
        onOpenChange={onOpenChange}
        tour="store-catalog.add.dialog"
        title={t('storeCatalog:add.title')}
        storeId={storeId}
        statuses={PICKABLE}
        memberOf={{ kind: 'store', id: storeId }}
        lockedLabel={t('storeCatalog:add.alreadyHere')}
        selectAllMatching
        footerContent={(selection) => footer(selection.items)}
        onConfirm={(selection, productSelection) => confirm(selection.items, productSelection)}
      />
      {selectionRequest.dialog}
      <Dialog open={outcome !== null} onOpenChange={(next) => !next && setOutcome(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('storeCatalog:add.title')}</DialogTitle>
            <DialogDescription className="sr-only">
              {t('storeCatalog:add.description')}
            </DialogDescription>
          </DialogHeader>
          {outcome ? <SkippedSummary {...outcome} /> : null}
          <DialogFooter>
            <Button type="button" onClick={() => setOutcome(null)}>
              {t('storeCatalog:add.done')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
